/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Iconify, MaskedValue } from "@repo/design-system/components/ui";
import { sentenceCase, buildPrimeMask } from '@repo/design-system/components/forms/utils';
import { NumberValueType, TextInputType } from "@repo/design-system/components/forms/libs";
import { InputMask } from "primereact/inputmask";

import PropTypes from 'prop-types';

import "./Input.scss";

const excptIntSymbol = ["e", "E", "+", "-"];
const excptDecimalSymbol = ["e", "E", "+", "-"];

/**
 * A customizable input component that supports various input types and validation.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.type - The type of input field (e.g., text, number).
 * @param {string} [props.name=""] - The name attribute for the input field.
 * @param {string} [props.placeholder=`${name}`] - The placeholder text for the input field.
 * @param {string|number} props.value - The value of the input field.
 * @param {number} [props.min] - The minimum value (for number inputs).
 * @param {number} [props.max] - The maximum value (for number inputs).
 * @param {Function} props.onChange - Callback function when input value changes.
 * @param {Function} [props.onKeyup] - Callback function when a key is released.
 * @param {Function} [props.onBlur] - Callback function when the input loses focus.
 * @param {number} [props.minLength] - The minimum length of the input.
 * @param {number} [props.maxLength] - The maximum length of the input.
 * @param {string} [props.cssClass="input-field"] - CSS class for styling the input.
 * @param {string} [props.valueType] - The type of value (e.g., integer, decimal) for number inputs.
 * @param {boolean} [props.isDisable] - Whether the input is disabled.
 * @param {boolean} [props.isReadOnly] - Whether the input is read-only.
 * @param {RegExp} [props.pattern] - Regular expression pattern for validation.
 * @param {string} [props.mask] - Optional mask pattern (e.g., '00-000', '000.00').
 * @param {string} [props.percentageFormat] - Optional format string for percentage inputs.
 * @param {{format: string}} [props.masked] - Optional dynamic mask config, e.g. {format: "XXXXX-0000-X"} (X = alpha, 0 = digit, other chars = fixed separators).
 * @param {boolean} [props.isUpperCase] - When true, forces the typed value to uppercase.
 * @param {boolean} [props.keepPlaceholderCase] - When true, shows the placeholder as written (no sentence-casing) — for acronyms, URLs and brand names.
 */
const Input = ({
    id, type = TextInputType.TEXT, name = "", placeholder,
    value, min, max, onChange, onKeyup, onBlur, minLength, maxLength,
    cssClass = "input-field", valueType, isdisable, isreadonly,
    pattern, iconConfig, clearIconConfig, mainClass, isAllowSpace = false,
    mask, percentageFormat, isMasked = false, maskedType = "", masked,
    isUpperCase = false, keepPlaceholderCase = false
}) => {

    const [showPassword, setShowPassword] = useState(false);
    const [inputAttributes, setInputAttributes] = useState({});
    const [localValue, setLocalValue] = useState("");
    const [localPlainValue, setLocalPlainValue] = useState(value ?? "");

    const placeholderText = keepPlaceholderCase ? (placeholder || name) : sentenceCase(placeholder || name);

    const legacyMask = mask || percentageFormat;
    const dynamicMaskFormat = masked?.format;
    const activeMask = legacyMask || dynamicMaskFormat;

    const decimalFormat = percentageFormat || (dynamicMaskFormat && !/[Xx]/.test(dynamicMaskFormat) && dynamicMaskFormat.includes('.') ? dynamicMaskFormat : null);
    const padDecimalValue = (val) => {
        if (typeof val !== "number" || !decimalFormat) return val;
        const decimalDigits = decimalFormat.split(".")?.[1]?.length ?? 2;
        return val.toFixed(decimalDigits);
    };

    const cleanMaskedValue = (val) => {
        if (!val) return "";
        const modifiedVal = String(padDecimalValue(val));
        let clean = modifiedVal.replace(/_/g, "");
        clean = clean.replace(/[^a-zA-Z0-9]+$/, "");
        return /[a-zA-Z0-9]/.test(clean) ? clean : "";
    };

    const sameContent = (a, b) => (isUpperCase ? String(a).toLowerCase() === String(b).toLowerCase() : a === b);

    useEffect(() => {
        if (activeMask) {
            const cleanProp = cleanMaskedValue(value);
            const cleanLocal = cleanMaskedValue(localValue);
            if (cleanProp === "") {
                setLocalValue("");
            } else if (!sameContent(cleanProp, cleanLocal)) {
                setLocalValue(padDecimalValue(value) ?? "");
            }
        }
    }, [value, activeMask, isUpperCase]);

    useEffect(() => {
        if (!activeMask && isUpperCase && !sameContent(value ?? "", localPlainValue ?? "")) {
            setLocalPlainValue(value ?? "");
        }
    }, [value, activeMask, isUpperCase]);

    const updateAttributes = () => {
        const newAttribute = { ...inputAttributes };

        if (minLength) {
            newAttribute.minLength = minLength;
        }
        if (maxLength) {
            newAttribute.maxLength = maxLength;
        }

        if (isreadonly) {
            newAttribute.readOnly = isreadonly;
        }

        if (type === TextInputType.NUMBER && valueType === NumberValueType.INT) {
            newAttribute.step = 1;
        }

        setInputAttributes(newAttribute);
    };

    useEffect(() => {
        updateAttributes();
    }, [minLength, maxLength, type, valueType]);

    const handleInputChange = (e) => {
        let inputValue = e.target.value;

        if (type === TextInputType.NUMBER && maxLength) {
            inputValue = inputValue.slice(0, maxLength);
        }

        if (isUpperCase) {
            setLocalPlainValue(inputValue); // keep as-typed for display - avoids caret jump
            e.target.value = inputValue.toUpperCase(); // real (uppercase) value sent to the model
        } else {
            e.target.value = inputValue;
        }
        onChange(e);
    };

    const handlePlainBlur = (e) => {
        if (isUpperCase) {
            setLocalPlainValue((e.target.value || "").toUpperCase());
        }
        if (onBlur) {
            onBlur(e);
        }
    };

    const handleMaskChange = (e) => {
        const rawVal = e.target.value || ""; // keep as-typed for display - avoids caret jump
        setLocalValue(rawVal);

        const cleanVal = rawVal.replace(/_/g, "");
        let finalVal = /[a-zA-Z0-9]/.test(cleanVal) ? cleanVal : "";
        if (isUpperCase) {
            finalVal = finalVal.toUpperCase();
        }
        e.target.value = finalVal;
        onChange(e);
    };

    const handleMaskBlur = (e) => {
        const rawVal = e.target.value || "";
        const cleanVal = rawVal.replace(/_/g, "");
        let finalVal = /[a-zA-Z0-9]/.test(cleanVal) ? cleanVal : "";
        if (isUpperCase) {
            finalVal = finalVal.toUpperCase();
        }

        setLocalValue(finalVal); // Strip underscores on blur for visual correctness

        e.target.value = finalVal;
        if (onBlur) {
            onBlur(e);
        }
    };

    const handleKeyDown = (e) => {
        if (isAllowSpace == 'true' && e.keyCode === 32) {
            e.preventDefault();
            return;
        }

        if (type === TextInputType.NUMBER && valueType === NumberValueType.INT && excptIntSymbol.includes(e.key)) {
            e.preventDefault();
            return;
        }

        if (type === TextInputType.NUMBER && valueType === NumberValueType.DECIMAL && excptDecimalSymbol.includes(e.key)) {
            e.preventDefault();
        }
    };

    const toggleShowPassword = () => {
        setShowPassword(prevShowPassword => !prevShowPassword);
    };

    // if (isMasked) {
    //     return (
    //         <MaskedValue value={value}
    //             maskedType={maskedType}
    //             targetId={name} />
    //     )
    // }

    if (activeMask) {
        // legacy 'mask'/'percentageFormat' configs only ever use '0' for digit slots;
        // 'masked.format' additionally supports 'X'/'x' for alpha-only slots.
        const prMask = legacyMask ? legacyMask.replace(/0/g, '9') : buildPrimeMask(dynamicMaskFormat);

        return (
            <div className={`input-forms ${mainClass ?? ""}`}>
                <InputMask
                    id={id}
                    value={localValue ?? ""}
                    name={name}
                    mask={prMask}
                    slotChar="_"
                    autoClear={false}
                    className={cssClass}
                    style={isUpperCase ? { textTransform: 'uppercase' } : undefined}
                    placeholder={placeholderText}
                    onChange={handleMaskChange}
                    onBlur={handleMaskBlur}
                    onKeyUp={onKeyup}
                    onKeyDown={handleKeyDown}
                    disabled={isdisable}
                    readOnly={isreadonly}
                    autoComplete="new-password"
                    {...inputAttributes}
                />
                {iconConfig?.icon && (
                    <div className="icon-fix">
                        <div className="input-icon right">
                            <Iconify icon={iconConfig?.icon} className="button-icon"
                                {...iconConfig} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`input-forms ${mainClass ?? ""}`}>
            <input
                id={id}
                value={isUpperCase ? (localPlainValue ?? "") : value}
                name={name}
                type={showPassword && type === TextInputType.PASSWORD ? TextInputType.TEXT : type}
                className={cssClass}
                style={isUpperCase ? { textTransform: 'uppercase' } : undefined}
                placeholder={placeholderText}
                onChange={handleInputChange}
                onKeyUp={onKeyup}
                onKeyDown={handleKeyDown}
                onBlur={handlePlainBlur}
                onWheel={(e) => {
                    if (type === TextInputType.NUMBER) {
                        e.target.blur();
                    }
                }}
                disabled={isdisable}
                min={min}
                max={max}
                readOnly={isreadonly}
                pattern={pattern}
                autoComplete="new-password"
                {...inputAttributes}
            />
            {clearIconConfig?.icon && (
                <div className="icon-fix">
                    <div className="input-icon input-clear-icon right-20">
                        <Iconify icon={clearIconConfig?.icon} className="button-icon"
                            {...clearIconConfig} />
                    </div>
                </div>
            )}
            {iconConfig?.icon && (
                <div className="icon-fix">
                    <div className="input-icon right">
                        <Iconify icon={iconConfig?.icon} className="button-icon"
                            {...iconConfig} />
                    </div>
                </div>
            )}
            {type === TextInputType.PASSWORD && (
                <button type="button" className="password-hide-show" style={{ background: 'unset', border: 'unset' }} onClick={toggleShowPassword}>
                    <Iconify icon={showPassword ? "fluent:eye-off-16-regular" : "iconoir:eye"}
                        alt="eye" />
                </button>
            )}
        </div>
    );
};

Input.propTypes = {
    id: PropTypes.string,
    type: PropTypes.oneOf([TextInputType.TEXT, TextInputType.EMAIL, TextInputType.PASSWORD, TextInputType.NUMBER]).isRequired,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    onKeyup: PropTypes.func,
    onBlur: PropTypes.func,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    pattern: PropTypes.instanceOf(RegExp),
    cssClass: PropTypes.string,
    isdisable: PropTypes.bool,
    isreadonly: PropTypes.bool,
    valueType: PropTypes.oneOf([NumberValueType.INT, NumberValueType.DECIMAL]),
    iconConfig: PropTypes.object,
    isAllowSpace: PropTypes.bool,
    mask: PropTypes.string,
    percentageFormat: PropTypes.string,
    masked: PropTypes.shape({
        format: PropTypes.string
    }),
    isUpperCase: PropTypes.bool,
    keepPlaceholderCase: PropTypes.bool
};

export default Input;
